import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  X, ArrowRight, ArrowLeft, CheckCircle2, CreditCard, Loader2, AlertCircle, Award, ShieldCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const PACKAGE_ID_MAP = {
  // Mappa id del frontend (Packages.js) → id backend (routes_pagamenti.PACCHETTI)
  "tessera": "tessera",
  "tessera-corso": "tessera-corso",
  "premium": "premium",
  "registro": "premium", // legacy fallback
};

export default function IscrivitiModal({ pacchetto, onClose }) {
  const [step, setStep] = useState(1);
  const [authedUser, setAuthedUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", nome: "", cognome: "", telefono: "" });
  const [billing, setBilling] = useState({
    ragione_sociale: "",
    indirizzo: "",
    citta: "",
    cap: "",
    provincia: "",
    codice_fiscale: "",
    partita_iva: "",
    pec: "",
    sdi: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => {
        setAuthedUser(r.data);
        setForm((f) => ({ ...f, email: r.data.email, nome: r.data.nome, cognome: r.data.cognome }));
        setStep(2);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const isRegistro = pacchetto.id === "registro";
  const eligibleForRegistro = authedUser && (authedUser.cfdt_anno >= 20 || authedUser.corso_base_completato);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, {
        email: form.email, password: form.password,
        nome: form.nome, cognome: form.cognome, telefono: form.telefono,
      }, { withCredentials: true });
      setAuthedUser(data);
      setStep(2);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateBilling = () => {
    if (!billing.indirizzo.trim()) return "Indirizzo richiesto";
    if (!billing.citta.trim()) return "Città richiesta";
    if (!billing.cap.trim()) return "CAP richiesto";
    if (!billing.provincia.trim()) return "Provincia richiesta";
    if (!billing.codice_fiscale.trim() && !billing.partita_iva.trim())
      return "Inserisci almeno Codice Fiscale o Partita IVA";
    return null;
  };

  const handlePayment = async () => {
    setError("");
    const v = validateBilling();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const package_id = PACKAGE_ID_MAP[pacchetto.id] || pacchetto.id;
      const payload = {
        package_id,
        billing: {
          nome: authedUser?.nome || form.nome,
          cognome: authedUser?.cognome || form.cognome,
          email: authedUser?.email || form.email,
          telefono: authedUser?.telefono || form.telefono || undefined,
          indirizzo: billing.indirizzo,
          citta: billing.citta,
          cap: billing.cap,
          provincia: billing.provincia.toUpperCase(),
          codice_fiscale: billing.codice_fiscale || undefined,
          partita_iva: billing.partita_iva || undefined,
          ragione_sociale: billing.ragione_sociale || undefined,
          pec: billing.pec || undefined,
          sdi: billing.sdi || undefined,
        },
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/api/checkout/create-session`, payload, { withCredentials: true });
      // Salva il session_id e il pacchetto per il polling on success
      sessionStorage.setItem("acdt_last_session", JSON.stringify({
        session_id: data.session_id,
        package_id,
        package_nome: pacchetto.nome,
        amount: pacchetto.prezzo,
      }));
      window.location.href = data.url;
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message || "Errore avvio pagamento");
      setLoading(false);
    }
  };

  const Icon = pacchetto.icona;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-2xl overflow-hidden">
        <div className={`bg-gradient-to-br ${pacchetto.colore} text-white p-6 relative`}>
          <button onClick={onClose} data-testid="iscriviti-close" className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              {Icon && <Icon className="h-7 w-7" />}
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-white/80">{pacchetto.badge}</div>
              <h2 className="text-xl md:text-2xl font-extrabold leading-tight">{pacchetto.nome}</h2>
              <div className="mt-1 text-2xl font-extrabold">€{pacchetto.prezzo.toFixed(2)}<span className="text-[12px] font-semibold opacity-80">/anno</span></div>
            </div>
          </div>
        </div>

        <div className="px-6 pt-5 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-extrabold ${
                  step >= s ? "bg-[#102a6b] text-white" : "bg-slate-200 text-slate-500"
                }`}>
                  {step > s ? <CheckCircle2 className="h-4 w-4" /> : s}
                </div>
                {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? "bg-[#102a6b]" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500">
            <span className={step === 1 ? "text-[#102a6b]" : ""}>Account</span>
            <span className={step === 2 ? "text-[#102a6b]" : ""}>Dati per fatturazione</span>
            <span className={step === 3 ? "text-emerald-600" : ""}>Pagamento</span>
          </div>
        </div>

        <div className="p-6">
          {/* STEP 1: Account */}
          {step === 1 && (
            <>
              {isRegistro && !authedUser && (
                <div className="mb-5 rounded-xl bg-[#f59e0b]/10 border border-[#f59e0b]/30 p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-[#b45309] flex-shrink-0 mt-0.5" />
                  <div className="text-[12.5px] text-[#7c2d12]">
                    <strong>Requisiti Registro:</strong> dopo la registrazione, l&apos;iscrizione al Registro sarà attivata
                    solo dopo aver completato il Corso A.C.D.T. per la qualificazione professionale OPPURE raggiunto 20 CFDT.
                  </div>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nome *">
                    <Input data-testid="iscr-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Mario" />
                  </Field>
                  <Field label="Cognome *">
                    <Input data-testid="iscr-cognome" required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} placeholder="Rossi" />
                  </Field>
                </div>
                <Field label="Email *">
                  <Input data-testid="iscr-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@email.it" />
                </Field>
                <Field label="Password *">
                  <Input data-testid="iscr-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 caratteri" />
                </Field>
                <Field label="Telefono (opzionale)">
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 333 1234567" />
                </Field>

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-[12.5px] font-semibold">{error}</div>
                )}

                <Button type="submit" disabled={loading} data-testid="iscr-next-step1" className="w-full h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continua <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>

                <p className="text-center text-[12px] text-slate-500">
                  Hai già un account?{" "}
                  <button type="button" onClick={() => { onClose(); navigate("/area-soci"); }} className="text-[#102a6b] font-bold hover:underline">
                    Accedi prima
                  </button>
                </p>
              </form>
            </>
          )}

          {/* STEP 2: Dati per fatturazione + Pagamento */}
          {step === 2 && (
            <>
              {isRegistro && authedUser && !eligibleForRegistro && (
                <div className="mb-5 rounded-xl bg-amber-50 border-2 border-amber-300 p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="text-[13px] text-amber-900">
                    <strong>Procederai con la prenotazione</strong> ma non sei ancora eleggibile al Registro.
                    Sarà attivato quando completi i requisiti (corso base o 20 CFDT).
                  </div>
                </div>
              )}

              <div className="mb-5 rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-wider font-extrabold text-emerald-700">Account verificato</div>
                  <div className="font-bold text-[#0a1f55]">{authedUser?.nome || form.nome} {authedUser?.cognome || form.cognome}</div>
                  <div className="text-[12px] text-slate-500">{authedUser?.email || form.email}</div>
                </div>
              </div>

              {/* SEZIONE: DATI PER FATTURAZIONE */}
              <div data-testid="billing-section" className="rounded-xl border-2 border-[#102a6b]/20 bg-white p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-[#102a6b] text-white flex items-center justify-center">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-[#0a1f55] text-[15px]">Dati per fatturazione</h3>
                    <p className="text-[11.5px] text-slate-500">Necessari per emettere la ricevuta del pagamento</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Field label="Ragione sociale (se diversa da nome e cognome)">
                    <Input data-testid="bill-ragione" value={billing.ragione_sociale}
                      onChange={(e) => setBilling({ ...billing, ragione_sociale: e.target.value })}
                      placeholder="Lascia vuoto se persona fisica" />
                  </Field>
                  <Field label="Indirizzo *">
                    <Input data-testid="bill-indirizzo" required value={billing.indirizzo}
                      onChange={(e) => setBilling({ ...billing, indirizzo: e.target.value })}
                      placeholder="Via Roma 1" />
                  </Field>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Field label="Città *">
                        <Input data-testid="bill-citta" required value={billing.citta}
                          onChange={(e) => setBilling({ ...billing, citta: e.target.value })}
                          placeholder="Milano" />
                      </Field>
                    </div>
                    <Field label="CAP *">
                      <Input data-testid="bill-cap" required value={billing.cap}
                        onChange={(e) => setBilling({ ...billing, cap: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                        placeholder="20100" maxLength={5} />
                    </Field>
                  </div>
                  <Field label="Provincia (sigla) *">
                    <Input data-testid="bill-provincia" required value={billing.provincia}
                      onChange={(e) => setBilling({ ...billing, provincia: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="MI" maxLength={2} className="uppercase" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Codice Fiscale">
                      <Input data-testid="bill-cf" value={billing.codice_fiscale}
                        onChange={(e) => setBilling({ ...billing, codice_fiscale: e.target.value.toUpperCase() })}
                        placeholder="RSSMRA80A01H501Z" className="uppercase" />
                    </Field>
                    <Field label="Partita IVA">
                      <Input data-testid="bill-piva" value={billing.partita_iva}
                        onChange={(e) => setBilling({ ...billing, partita_iva: e.target.value.replace(/\D/g, "") })}
                        placeholder="12345678901" />
                    </Field>
                  </div>
                  <div className="text-[11.5px] text-slate-500 -mt-2">Inserisci almeno uno tra Codice Fiscale e P.IVA.</div>
                  <details className="rounded-lg border border-slate-200 p-3 text-[12.5px]">
                    <summary className="cursor-pointer font-bold text-[#0a1f55]">Fatturazione elettronica (PEC / SDI) — opzionale</summary>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <Field label="PEC">
                        <Input data-testid="bill-pec" value={billing.pec}
                          onChange={(e) => setBilling({ ...billing, pec: e.target.value })}
                          placeholder="azienda@pec.it" />
                      </Field>
                      <Field label="Codice SDI">
                        <Input data-testid="bill-sdi" value={billing.sdi}
                          onChange={(e) => setBilling({ ...billing, sdi: e.target.value.toUpperCase().slice(0, 7) })}
                          placeholder="XXXXXXX" className="uppercase" maxLength={7} />
                      </Field>
                    </div>
                  </details>
                </div>
              </div>

              {/* Riepilogo */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-4">
                <div className="flex justify-between items-center mb-2 text-[13px]">
                  <span className="text-slate-600">{pacchetto.nome}</span>
                  <span className="font-bold text-[#0a1f55]">€ {pacchetto.prezzo.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="font-extrabold text-[#0a1f55]">Totale</span>
                  <span className="font-extrabold text-2xl text-[#0a1f55]">€ {pacchetto.prezzo.toFixed(2)}</span>
                </div>
              </div>

              {/* Sicurezza badge */}
              <div className="flex items-center gap-2 text-[11.5px] text-slate-500 mb-4">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                Pagamento sicuro con <strong className="text-[#0a1f55]">Stripe</strong>. Accettiamo Visa, Mastercard, Amex, Apple Pay, Google Pay.
              </div>

              {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-[12.5px] mb-3">{error}</div>}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => !authedUser ? setStep(1) : onClose()} className="border-slate-300">
                  <ArrowLeft className="mr-2 h-4 w-4" /> {authedUser ? "Annulla" : "Indietro"}
                </Button>
                <Button data-testid="iscr-pay-confirm" disabled={loading} onClick={handlePayment} className="flex-1 h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                  {loading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <><CreditCard className="mr-2 h-4 w-4" /> Paga ora € {pacchetto.prezzo.toFixed(2)} <ArrowRight className="ml-2 h-4 w-4" /></>
                  }
                </Button>
              </div>
            </>
          )}

          {/* STEP 3 — non più usato (success page gestita su /checkout/success) */}
          {step === 3 && (
            <div className="text-center py-6">
              <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-5">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#0a1f55]">Pagamento completato!</h3>
              <div className="mt-7 flex gap-2 justify-center">
                <Button onClick={() => { onClose(); navigate("/dashboard"); }} data-testid="iscr-go-dashboard" className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                  Vai alla Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] font-semibold text-[#0a1f55]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
